import "./PaymentsComponent.scss";
import { useCallback, useState, useEffect } from "react";

import { useSelector } from "react-redux";
import { IRootReducerState } from "@/store/reducers";
import { useAppSelector } from "@/store/hooks.ts";
import PaymentCardSkeleton from "@/components/common-components/loading-skeleton/PaymentCardSkeleton";
import { CheckIcon } from "lucide-react";
import { Button } from "@/components/ui/button.tsx";
import { CommonService } from "@/services";
import { Misc } from "@/constants";
import { IAPIResponseType } from "@/models/api.model";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { userLogin } from "@/store/actions/auth.action";
import { getSubscriptions, getUsersTokensUtilization } from "@/store/actions/meta.action";
import FAQComponent from "./FAQComponent";
import ENV from "@/constants/ENV";

// Type definitions for the new subscription structure

interface CostProvider {
    plan_id: string;
    is_active: boolean;
}

interface CostStructure {
    currency: string;
    amount: number;
    providers: {
        razorpay: CostProvider;
    } | null;
}

interface PlanDetails {
    description: string; // Add description field
    features: string[]; // Change to string array
    cost_structure: {
        IN: CostStructure;
        US?: CostStructure;
        GLOBAL: CostStructure;
    };
}

interface SubscriptionPlan {
    id: string;
    plan_name: string;
    plan_name_tag: string;
    plan_interval: string;
    plan_details: PlanDetails;
    is_public_plan: boolean;
    allowed_spend_budget_USD: number;
    is_topup_plan: boolean;
    is_domestic: boolean;
    created_at: string;
    updated_at: string;
}

// interface RazorpayResponse {
//     razorpay_subscription_id: string;
//     [key: string]: any;
// }

// Extend Window interface for Razorpay
declare global {
    interface Window {
        Razorpay: any;
    }
}

// Plan hierarchy mapping based on plan_id (outside component to avoid dependency issues)
const PLAN_HIERARCHY = {
    'test_id': 0,      // Free tier
    'pro_id': 1,       // Pro tier
    'advisory_id': 2,  // Advisory/Enterprise tier
    'startup_id': 3    // Startup tier (highest)
};

const PaymentsComponent = () => {

    const { subscriptions, subscriptionsLoading, subscriptionsError } = useSelector((state: IRootReducerState) => state.meta);
    const [isGeneratingSubscription, setIsGeneratingSubscription] = useState<boolean>(false)
    const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null)
    const { user } = useAppSelector((state) => state.auth);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [activeTab, setActiveTab] = useState<'ai-agents' | 'ai-human'>('ai-agents');
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const token = CommonService._localStorage.getItem(Misc.LOCAL_STORAGE_JWT_TOKEN);

    const currentUser = user

    // Get current user's plan tier level
    const getCurrentPlanLevel = useCallback(() => {
        const currentPlanId = currentUser?.subscription?.plan_id;
        if (!currentPlanId) return -1; // No subscription
        return PLAN_HIERARCHY[currentPlanId as keyof typeof PLAN_HIERARCHY] ?? -1;
    }, [currentUser]);

    // Check if a plan should be disabled (current or lower tier)
    const isPlanDisabled = useCallback((planId: string) => {
        const currentLevel = getCurrentPlanLevel();
        const planLevel = PLAN_HIERARCHY[planId as keyof typeof PLAN_HIERARCHY];
        
        // Disable if planLevel is undefined or if it's current or lower tier
        return planLevel === undefined || planLevel <= currentLevel;
    }, [getCurrentPlanLevel]);

    // Load subscriptions when component mounts
    useEffect(() => {
        dispatch(getSubscriptions());
    }, [dispatch]);

    const fetchUserDetails = useCallback(() => {
        const token = CommonService._localStorage.getItem(Misc.LOCAL_STORAGE_JWT_TOKEN);
        if (token) {
            CommonService._account.CheckLoginAPICall(token)
                .then((response: IAPIResponseType<any>) => {
                    setIsLoading(false)
                    dispatch(userLogin(response?.data, token, ""));
                    navigate("/")
                })
                .catch(() => {
                    dispatch(userLogin(null, "", ""));
                    setIsLoading(false);
                })
        } else {
            setIsLoading(false);
        }
    }, [dispatch,navigate])

    const checkTransaction = useCallback((subscriptionId: string) => {
        CommonService._payments_Service.checkTransaction(subscriptionId )
            .then((response: IAPIResponseType<any>) => {
                // Track successful payment
                CommonService.handleMixpanelTrack('payment_success', {
                    transaction_id: subscriptionId,
                    plan_id: selectedPlan?.id,
                    plan_name: selectedPlan?.plan_name,
                    amount: selectedPlan ? getPriceForRegion(selectedPlan).amount : 0,
                    currency: selectedPlan ? getPriceForRegion(selectedPlan).currency : 'USD',
                    timestamp: new Date().toISOString()
                });
                
                CommonService._alert.showToast(response?.data?.message || response?.message || "Transaction completed successfully!")
                // Show additional message about payment processing time
                fetchUserDetails()
                setTimeout(() => {
                    dispatch(getUsersTokensUtilization())   
                }, 500);
                setIsLoading(false); // Reset loading state on success
            })
            .catch((error: IAPIResponseType<any>) => {
                // Track failed payment
                CommonService.handleMixpanelTrack('payment_failed', {
                    transaction_id: subscriptionId,
                    plan_id: selectedPlan?.id,
                    plan_name: selectedPlan?.plan_name,
                    amount: selectedPlan ? getPriceForRegion(selectedPlan).amount : 0,
                    currency: selectedPlan ? getPriceForRegion(selectedPlan).currency : 'USD',
                    error_message: error?.message || "Transaction failed",
                    timestamp: new Date().toISOString()
                });
                
                setIsLoading(false);
                CommonService._alert.showToast(error?.message || "An error occurred", "error")
            })
    }, [fetchUserDetails, selectedPlan, dispatch])

    const handleSubscribe = useCallback(async (transactionId: string,subscription_id:string) => {
        try {
            // Track payment initiation
            CommonService.handleMixpanelTrack('payment_initiated', {
                transaction_id: transactionId,
                plan_id: selectedPlan?.id,
                plan_name: selectedPlan?.plan_name,
                amount: selectedPlan ? getPriceForRegion(selectedPlan).amount : 0,
                currency: selectedPlan ? getPriceForRegion(selectedPlan).currency : 'USD',
                payment_provider: 'razorpay',
                timestamp: new Date().toISOString()
            });

            const options = {
                key: ENV.RAZORPAY_KEY || 'rzp_live_PQjOObQvLhhMOS',
                subscription_id: subscription_id,
                name: 'SuperEngineer',
                description: 'Subscription Payment',
                handler: function () {
                    setIsLoading(true);

                    // Add 2 second delay before checking transaction
                    setTimeout(() => {
                        checkTransaction(transactionId)
                    }, 5000);
                },
                prefill: {
                    name: currentUser?.name,
                    email: currentUser?.email,
                    contact: ''
                },
                theme: {
                    color: '#3399cc'
                },
                modal: {
                    ondismiss: function() {
                        // Track payment cancellation
                        CommonService.handleMixpanelTrack('payment_cancelled', {
                            transaction_id: transactionId,
                            plan_id: selectedPlan?.id,
                            plan_name: selectedPlan?.plan_name,
                            amount: selectedPlan ? getPriceForRegion(selectedPlan).amount : 0,
                            currency: selectedPlan ? getPriceForRegion(selectedPlan).currency : 'USD',
                            payment_provider: 'razorpay',
                            timestamp: new Date().toISOString()
                        });
                    }
                }
            };
            const rzp = new window.Razorpay(options);
            rzp.open();
        } catch (error) {
            // Track payment initiation error
            CommonService.handleMixpanelTrack('payment_initiation_failed', {
                transaction_id: transactionId,
                plan_id: selectedPlan?.id,
                plan_name: selectedPlan?.plan_name,
                amount: selectedPlan ? getPriceForRegion(selectedPlan).amount : 0,
                currency: selectedPlan ? getPriceForRegion(selectedPlan).currency : 'USD',
                error_message: error?.message || "Failed to initiate payment",
                timestamp: new Date().toISOString()
            });
            console.error('Error creating subscription:', error);
        }
    }, [checkTransaction, currentUser, selectedPlan]);

    const generateSubscription = useCallback((plan: SubscriptionPlan) => {
        // For now, open mail logic for all plans
        // window.location.href = `mailto:info@superengineer.ai?subject=${plan.plan_name.charAt(0).toUpperCase() + plan.plan_name.slice(1)} Plan Inquiry&body=Hi, I'm interested in learning more about the ${plan.plan_name.charAt(0).toUpperCase() + plan.plan_name.slice(1)} Plan.`;
        if (!token) {
            // dispatch(setSystemLocked(false, undefined));
            navigate('/login?returnUrl=' + encodeURIComponent(location.pathname + location.search));
        }
        const payload = {
            plan_id: plan?.id
        }
        setSelectedPlan(plan)
        setIsGeneratingSubscription(true)
        CommonService._payments_Service.createTransaction(payload)
            .then((response: any) => {
                handleSubscribe(response?.data?.transaction_id,response?.data?.rz_transaction_id)
                setSelectedPlan(null)
                setIsGeneratingSubscription(false)
            })
            .catch((error: any) => {
                // Track subscription generation failure
                CommonService.handleMixpanelTrack('subscription_generation_failed', {
                    plan_id: plan?.id,
                    plan_name: plan?.plan_name,
                    amount: getPriceForRegion(plan).amount,
                    currency: getPriceForRegion(plan).currency,
                    error_message: error?.message || "Failed to generate subscription",
                    timestamp: new Date().toISOString()
                });
                
                setSelectedPlan(null)
                // commonService._alert.showToast(error.message, "error")
                setIsGeneratingSubscription(false)
            })
    }, [handleSubscribe, token, navigate])

    // Helper function to get price for current user's region
    const getPriceForRegion = (plan: SubscriptionPlan) => {
        // Add null checks to prevent crashes
        if (!plan?.plan_details?.cost_structure) {
            return { currency: 'USD', amount: 0, providers: null };
        }

        const costStructure = plan.plan_details.cost_structure;

        // Use is_domestic field to determine pricing
        // If is_domestic is true, show INR pricing, otherwise show USD pricing
        if (plan.is_domestic && costStructure.IN) {
            return costStructure.IN;
        } else if (costStructure.US) {
            return costStructure.US;
        } else {
            return costStructure.GLOBAL || { currency: 'USD', amount: 0, providers: null };
        }
    }

    // Filter plans based on active tab
    const getFilteredPlans = () => {
        if (!subscriptions || !Array.isArray(subscriptions)) {
            return [];
        }

        return subscriptions.filter((plan: SubscriptionPlan) => {
            const planName = plan.plan_name?.toLowerCase() || '';
            
            if (activeTab === 'ai-agents') {
                // AI Agents tab: Free and Pro plans
                return planName === 'free' || planName === 'pro';
            } else {
                // AI Agent + Human Support tab: Advisory, Startup plans
                return planName === 'advisory' || planName === 'startup';
            }
        });
    };

    // Helper function to get button text
    const getButtonText = useCallback((subscriptionData: SubscriptionPlan) => {
        const currentPlanId = currentUser?.subscription?.plan_id;
        
        // Check if this is the current plan
        if (subscriptionData.id === currentPlanId) {
            return 'Current Plan';
        }
        
        // Check if it's a lower tier plan
        if (isPlanDisabled(subscriptionData.id)) {
            return 'Lower Tier';
        }
        
        if (isLoading) {
            return 'Processing...';
        }
        
        return 'Subscribe';
    }, [currentUser, isPlanDisabled, isLoading]);

    const filteredPlans = getFilteredPlans();



    return (
        <div className="payments-component">

            <div className="payments-header">
                <div className={"payments-title"}>Upgrade Your <span
                    className={'highlight-text'}>Plan </span> to Unlock <br /> more Possibilities
                </div>
                <div className={"payments-description"}>Upgrade your plan to <span
                    className={'highlight-secondary-text'}>unlock more features</span> and
                    resources.Choose the right tier for<span className={'highlight-secondary-text'}> greater flexibility, support, and scalability.</span>
                </div>
            </div>

            {/* Tab Navigation */}
            <div className="pricing-tabs">
                <button 
                    className={`tab-button ${activeTab === 'ai-agents' ? 'active' : ''}`}
                    onClick={() => {
                        setActiveTab('ai-agents');
                        CommonService.handleMixpanelTrack('pricing_tab_switched', { tab: 'ai-agents' });
                    }}
                >
                    AI Agents
                </button>
                <button 
                    className={`tab-button ${activeTab === 'ai-human' ? 'active' : ''}`}
                    onClick={() => {
                        setActiveTab('ai-human');
                        CommonService.handleMixpanelTrack('pricing_tab_switched', { tab: 'ai-human' });
                    }}
                >
                    AI Agents + Human Support  
                </button>
            </div>

            <div className="payment-card-wrapper">
                {subscriptionsLoading ? (
                    <PaymentCardSkeleton count={2} />
                ) : subscriptionsError ? (
                    <div className="error-state">
                        <div className="error-message">
                            <h3>Failed to load subscription plans</h3>
                            <p>{subscriptionsError}</p>
                            <button 
                                onClick={() => {
                                    CommonService.handleMixpanelTrack('pricing_retry_button_clicked');
                                    window.location.reload();
                                }} 
                                className="retry-button"
                            >
                                Try Again
                            </button>
                        </div>
                    </div>
                ) : !subscriptions || subscriptions.length === 0 ? (
                    <div className="empty-state">
                        <div className="empty-message">
                            <h3>No subscription plans available</h3>
                            <p>Please try again later or contact support.</p>
                        </div>
                    </div>
                ) : (
                    filteredPlans.map((subscriptionData: SubscriptionPlan) => {
                        // Add null checks to prevent crashes
                        if (!subscriptionData || !subscriptionData.plan_details) {
                            return null; // Skip rendering if data is invalid
                        }

                        const priceInfo = getPriceForRegion(subscriptionData);
                        const features = subscriptionData.plan_details.features || [];
                        const description = subscriptionData.plan_details.description || '';
                        const planTagName = subscriptionData.plan_name_tag || '';

                        return (
                            <div key={subscriptionData.id} className={"payment-card"}>
                                {subscriptionData.plan_name?.toLowerCase().startsWith('startup') && priceInfo?.currency === 'INR' ?(
                                    <div className="popular-tag">Popular</div>
                                ): null}
                                <div className="plan-content-wrppaer">
                                    <div className="plan-details">
                                        <div className={'plan-name'}>{subscriptionData.plan_name ? subscriptionData.plan_name.charAt(0).toUpperCase() + subscriptionData.plan_name.slice(1) : 'Plan'}</div>
                                        <div className={'plan-tag'}>
                                            {planTagName}
                                        </div>
                                        <div className={'plan-description'}>
                                            {description}
                                        </div>
                                    </div>
                                    <div className="price-wrapper">
                                    {subscriptionData.plan_name?.toLowerCase().startsWith('startup')  ? (
                                        <div className="discount-price">
                                            <span className="original-price">{priceInfo?.currency === 'INR' ? '₹2,00,000' : '$4,000'}</span>
                                            <div className="discounted-price">
                                                <span>{priceInfo?.currency === 'INR' ? '₹' : '$'}{priceInfo?.amount?.toLocaleString() || 0}</span>
                                                <span className="duration-wrapper">
                                                    /{subscriptionData.plan_interval || 'month'}
                                                </span>
                                            </div>
                                        </div>
                                    ) :
                                    
                                     (
                                        <>
                                            {priceInfo?.currency === 'INR' ? '₹' : '$'}{priceInfo?.amount?.toLocaleString() || 0}
                                            <span className="duration-wrapper">
                                                /{subscriptionData.plan_interval || 'month'}
                                            </span>
                                        </>
                                    )}
                                </div>
                                    <div className="footer-wrapper">
                                        <Button
                                            id={"pricing-" + subscriptionData.plan_name}
                                            fullWidth={true}
                                            disabled={isPlanDisabled(subscriptionData.id)}
                                            onClick={() => {
                                                // Open Calendly for Startup plan
                                                if (subscriptionData.plan_name?.toLowerCase().startsWith('startup')) {
                                                    window.open('https://calendly.com/abdulrahmanjanoo/30min', '_blank');
                                                } else {
                                                    generateSubscription(subscriptionData);
                                                }
                                            }}
                                            loading={isGeneratingSubscription && selectedPlan?.id === subscriptionData?.id || isLoading}
                                            mixpanelEvent={{
                                                name: subscriptionData.plan_name?.toLowerCase().startsWith('startup') 
                                                    ? 'pricing_schedule_call_clicked' 
                                                    : 'pricing_subscribe_button_clicked',
                                                properties: {
                                                    plan_name: subscriptionData.plan_name,
                                                    plan_id: subscriptionData.id,
                                                    currency: priceInfo?.currency,
                                                    amount: priceInfo?.amount,
                                                    interval: subscriptionData.plan_interval,
                                                    is_current_plan: subscriptionData.id === currentUser?.subscription?.plan_id,
                                                    is_disabled: isPlanDisabled(subscriptionData.id)
                                                }
                                            }}
                                        >
                                            {subscriptionData.plan_name?.toLowerCase().startsWith('startup') 
                                                ? 'Schedule a call' 
                                                : getButtonText(subscriptionData)
                                            }
                                        </Button>
                                    </div>
                                    <div className="feature-list">
                                        {Array.isArray(features) && features.map((feature: string, index: number) => (
                                            <div key={index} className={'feature-wrapper'}>
                                                <CheckIcon fontSize={"small"} />
                                                <span>{feature}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )
                    })
                )}
                {/* Enterprise plan - only show in AI Agent + Human Support tab */}
                {activeTab === 'ai-human' && (
                    <div key={'enterprise'} className={"payment-card"}>
                        <div className="plan-content-wrppaer">
                            <div className="plan-details">
                                <div className={'plan-name'}>Enterprise</div>
                                <div className={'plan-tag'}>Fully Customized</div>
                                <div className={'plan-description'}>For large organizations with unique requirements, advanced compliance needs, and large-scale deployments.</div>
                            </div>
                            <div className="price-wrapper">
                                <span>Custom Pricing</span>
                            </div>
                            <div className="footer-wrapper">
                                <Button
                                    id={"pricing-enterprise"}
                                    fullWidth={true}
                                    onClick={() => {
                                        window.open('https://calendly.com/abdulrahmanjanoo/30min', '_blank');
                                    }}
                                    mixpanelEvent={{
                                        name: 'pricing_enterprise_schedule_call_clicked',
                                        properties: {
                                            plan_name: 'Enterprise'
                                        }
                                    }}
                                >
                                    Schedule a call
                                </Button>
                            </div>

                            <div className="feature-list">
                                <div className="feature-wrapper">
                                    <CheckIcon fontSize={"small"} />
                                    <span>Unlimited projects</span>
                                </div>
                                <div className="feature-wrapper">
                                    <CheckIcon fontSize={"small"} />
                                    <span>Deploy on your own cloud or ours</span>
                                </div>
                                <div className="feature-wrapper">
                                    <CheckIcon fontSize={"small"} />
                                    <span>Custom app integrations</span>
                                </div>
                                {/* <div className="feature-wrapper">
                                    <CheckIcon fontSize={"small"} />
                                    <span>Integration with existing codebases</span>
                                </div>
                                <div className="feature-wrapper">
                                    <CheckIcon fontSize={"small"} />
                                    <span>Multi-database workflows</span>
                                </div> */}
                                <div className="feature-wrapper">
                                    <CheckIcon fontSize={"small"} />
                                    <span>Advanced security & compliance</span>
                                </div>
                                <div className="feature-wrapper">
                                    <CheckIcon fontSize={"small"} />
                                    <span>Dedicated onboarding team</span>
                                </div>
                                {/* <div className="feature-wrapper">
                                    <CheckIcon fontSize={"small"} />
                                    <span>Onboarding services</span>
                                </div> */}
                                <div className="feature-wrapper">
                                    <CheckIcon fontSize={"small"} />
                                    <span>Group-based access control</span>
                                </div>
                                <div className="feature-wrapper">
                                    <CheckIcon fontSize={"small"} />
                                    <span>Credits refill as per your needs</span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            {/* FAQ Section */}
            <FAQComponent />
        </div>
    );
};

export default PaymentsComponent;
